// @flow
/* eslint no-use-before-define:0 */

import pubSubMessage from 'app/common/redux/actions/pubSubMessage';
import store from 'app/common/redux/store/singleton';

type ChannelId = string;
type PubSubMessage = any;
type SubscriptionId = number;
type TopicId = string;
type SubscriptionHandler = (
  target: ChannelId,
  topic: TopicId,
  message: PubSubMessage,
  addingSubscriber?: boolean,
) => void;
type SubscriberOptions = {
  handler: SubscriptionHandler,
  name?: string,
  publishLastMessage?: boolean,
};

// Used to create unique tags on callbacks.
let COUNT: SubscriptionId = 0;

export class Subscriber {
  pubsub: PubSub;
  target: ChannelId;
  subscriberOptions: SubscriberOptions;
  topics: { [key: TopicId]: boolean };
  id: number;

  constructor(pubsub: PubSub, target: ChannelId, subscriberOptions: SubscriberOptions) {
    this.pubsub = pubsub;
    this.target = target;
    this.subscriberOptions = subscriberOptions;
    this.topics = {};
  }

  publish(topic: TopicId, message?: PubSubMessage, addingSubscriber?: boolean): Subscriber {
    if (addingSubscriber) {
      this.getHandler()(this.target, topic, message, addingSubscriber);
    } else {
      this.getHandler()(this.target, topic, message);
    }
    return this;
  }

  subscribe(...topics: Array<TopicId>) {
    this.getChannel().addSubscriber(this, topics, this.subscriberOptions.publishLastMessage);
    topics.forEach((topic) => {
      this.topics[topic] = true;
    });
    return this;
  }

  unsubscribe(...topics: Array<TopicId>) {
    if (topics.length === 0) {
      topics = this.getTopics();
    }

    topics.forEach((topic) => delete this.topics[topic]);

    this.getChannel().removeSubscriber(this, topics);

    return this;
  }

  getChannel() {
    return this.pubsub.getChannel(this.target);
  }

  getHandler() {
    return this.subscriberOptions.handler;
  }

  getName() {
    return this.subscriberOptions.name;
  }

  getId(): number {
    if (!this.id) {
      this.id = COUNT;
      COUNT += 1;
    }
    return this.id;
  }

  getTopics(): Array<TopicId> {
    return Object.keys(this.topics);
  }
}

class Channel {
  pubsub: PubSub;
  target: ChannelId;
  subscribers: Subscriber[];
  filters: {
    [keys: TopicId]: {
      [keys: SubscriptionId]: Subscriber,
    },
  };
  messages: { [keys: TopicId]: PubSubMessage };

  constructor(pubsub: PubSub, target: ChannelId) {
    this.pubsub = pubsub;
    this.target = target;
    this.subscribers = [];
    this.filters = {};
    this.messages = {};
  }

  addSubscriber(
    subscriber: Subscriber,
    topics: Array<TopicId>,
    publishLastMessage?: boolean,
  ): Subscriber {
    const subscriberId = subscriber.getId();

    this.subscribers[subscriberId] = subscriber;

    topics.forEach((topic) => {
      if (!this.filters[topic]) {
        this.filters[topic] = {};
      }

      // No need to add and publish messages if the subscriber
      // is already listenning to the topic.
      if (this.filters[topic][subscriberId]) {
        return;
      }

      // Add the subsciber to the filters for this topic
      this.filters[topic][subscriberId] = subscriber;

      // Publish the last message if one exists.
      if (publishLastMessage && this.messages[topic]) {
        subscriber.publish(topic, this.messages[topic], true);
      }
    });

    return subscriber;
  }

  removeSubscriber(subscriber: Subscriber, topics: Array<TopicId>) {
    const subscriberId = subscriber.getId();

    topics.forEach((topic) => {
      if (this.filters[topic]) {
        delete this.filters[topic][subscriberId];
      }
    });

    if (this.subscribers.length < 1) {
      this.pubsub.clearChannel(this.target);
    }
  }

  publish(topic: TopicId, message: PubSubMessage) {
    Object.values(this.filters[topic] || {}).forEach((subscriber) => {
      ((subscriber: any): Subscriber).publish(topic, message);
    });

    // Publish to any subscriber listening on all topics.
    if (topic !== '*') {
      this.publish('*', message);
    }

    this.messages[topic] = message;
  }

  getSubscribers(...topics: Array<TopicId>): Array<Subscriber> {
    if (!topics.length) {
      return this.subscribers;
    }

    const subscribers = {};

    topics.forEach((topic) => {
      Object.assign(subscribers, this.filters[topic]);
    });

    return ((Object.values(subscribers): any): Subscriber[]);
  }

  unsubscribeAll() {
    this.subscribers.forEach((subscriber) => {
      subscriber.unsubscribe();
    });
  }
}

/**
 * This implementation of PubSub saves the last value of
 * every published message on its channels. When you subscribe
 * to a channel, if there was a previous message, it will be
 * immediately be published to your handler.
 */
class PubSub {
  ns: string;
  channels: { [key: ChannelId]: Channel };

  constructor(ns: string) {
    this.ns = ns;
    this.channels = {};
  }

  _subscribe(
    target: ChannelId,
    topic: TopicId,
    handler: SubscriptionHandler,
    publishLastMessage: boolean,
  ) {
    if (__SERVER__) {
      // TODO: add back in Subscriber flow return type
      return { unsubscribe: () => {} };
    }
    const subscriber = new Subscriber(this, target, {
      handler,
      publishLastMessage,
    });
    subscriber.subscribe(topic);
    return subscriber;
  }

  /**
   * TL;DR: Please use `safeSubscribe` instead.
   *
   * This publishes an event immediately after subscribing. This is super hacky and is
   * known to cause bugs. Since this is a core part of the app and we are moving away
   * from it in favor of Redux, I am leaving it as is. Please use `safeSubscribe` instead.
   */
  subscribe(target: ChannelId, topic: TopicId, handler: SubscriptionHandler) {
    return this._subscribe(target, topic, handler, true);
  }

  /**
   * `subscribe` without hacks!
   */
  safeSubscribe(target: ChannelId, topic: TopicId, handler: SubscriptionHandler) {
    return this._subscribe(target, topic, handler, false);
  }

  publish(channel: ChannelId, topic: TopicId, message?: PubSubMessage) {
    if (__SERVER__) {
      return;
    }
    if (channel === PCONST.PUBSUB_CHANNEL_SITE && topic === PCONST.PUBSUB_TOPIC_NAVIGATE_STARTED) {
      // Processing every pubsub event is really slow, so we only
      // send events that we care about to redux.
      store.instance.dispatch(pubSubMessage(channel, topic, message));
    }
    this.getChannel(channel).publish(topic, message);
  }

  getChannel(target: ChannelId): Channel {
    if (!this.channels[target]) {
      this.channels[target] = new Channel(this, target);
    }
    return this.channels[target];
  }

  clearChannel(target: ChannelId) {
    const channel = this.channels[target];
    if (!channel) {
      return;
    }

    channel.unsubscribeAll();
    delete this.channels[target];
  }
}

const pubSub = {
  Subscriber,
  Channel,
  instance: new PubSub('local'),
};

export const instance = pubSub.instance;
export default pubSub;
